import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, CheckCircle2, Mail } from "lucide-react";
import { LEGAL_EMAIL } from "./legal";
import { useAuth } from "@/context/AuthContext";

const RED = "#C8000F";

export default function ContactUs() {
  const { user } = useAuth();
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (user) setF((p) => ({ ...p, name: p.name || user.name || "", email: p.email || user.email || "" }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || f.message.trim().length < 5) {
      toast.error("Please add your name, a valid email and a message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", f);
      setSent(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Couldn't send — please try again.");
    } finally {
      setSending(false);
    }
  };

  const inp = "w-full mt-1 bg-[#121823] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:border-[#C8000F] focus:outline-none";

  return (
    <div className="min-h-screen bg-[#0C1017] text-slate-100" style={{ fontFamily: "Inter, sans-serif" }} data-testid="contact-page">
      <header className="border-b border-white/10">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center"><img src="/rodeo-pilot-wordmark.webp" alt="Rodeo Pilot" className="h-10 w-auto object-contain" /></Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-300 hover:text-white"><ArrowLeft className="w-4 h-4" /> Back to home</Link>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-12">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">Contact us</h1>
        <p className="text-slate-400 mt-3">Questions, feedback, or data corrections? Send us a note and we'll get back to you. You can also email <a href={`mailto:${LEGAL_EMAIL}`} className="font-semibold" style={{ color: RED }}>{LEGAL_EMAIL}</a>.</p>

        {sent ? (
          <div className="mt-8 rounded-2xl border border-white/10 bg-[#121823] p-8 text-center" data-testid="contact-success">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: "#5FA83B" }} />
            <div className="font-display text-xl font-bold text-white">Message sent</div>
            <p className="text-sm text-slate-400 mt-2">Thanks for reaching out — we've got your message and will reply to {f.email}.</p>
            <Link to="/" className="inline-block mt-5 text-white font-bold px-5 py-2.5 rounded-xl" style={{ background: RED }}>Back to home</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 rounded-2xl border border-white/10 bg-[#121823] p-6 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block text-sm font-medium text-slate-300">Your name
                <input value={f.name} onChange={(e) => set("name", e.target.value)} className={inp} placeholder="e.g. Steele DePaoli" data-testid="contact-name" />
              </label>
              <label className="block text-sm font-medium text-slate-300">Email
                <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inp} placeholder="you@example.com" data-testid="contact-email" />
              </label>
            </div>
            <label className="block text-sm font-medium text-slate-300">Subject
              <input value={f.subject} onChange={(e) => set("subject", e.target.value)} className={inp} placeholder="What's this about?" data-testid="contact-subject" />
            </label>
            <label className="block text-sm font-medium text-slate-300">Message
              <textarea value={f.message} onChange={(e) => set("message", e.target.value)} rows={6} className={inp} placeholder="How can we help?" data-testid="contact-message" />
            </label>
            <button type="submit" disabled={sending} data-testid="contact-submit"
              className="w-full inline-flex items-center justify-center gap-2 font-bold py-3 rounded-xl text-white disabled:opacity-60 transition-transform hover:scale-[1.01]" style={{ background: RED }}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send message
            </button>
            <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> We typically reply within a couple of business days.</p>
          </form>
        )}
      </main>
    </div>
  );
}
